<div class="space-y-6 p-5">

  <!-- Header -->
  <div>
    <h2 class="text-sm font-semibold text-slate-900">Subscription Tier Limits</h2>
    <p class="mt-0.5 text-xs text-slate-500">
      Maximum number of monitored services per tier. Changes take effect on the next quota check.
    </p>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="flex items-center gap-2 text-sm text-slate-400">
    <lucide-icon name="loader" [size]="14" class="animate-spin" [strokeWidth]="2"></lucide-icon>
    Loading…
  </div>

  <!-- Error -->
  <div *ngIf="error" class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
    <lucide-icon name="triangle-alert" [size]="14" [strokeWidth]="2"></lucide-icon>
    {{ error }}
  </div>

  <!-- Tier cards -->
  <div *ngIf="!loading && !error" class="grid gap-4 sm:grid-cols-3">
    <div *ngFor="let tier of tiers"
      class="overflow-hidden rounded-xl border border-slate-200 bg-white">

      <!-- Card header -->
      <div class="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <span class="rounded-full px-2.5 py-0.5 text-xs font-semibold" [class]="tierBadgeClass(tier.tier)">
          {{ tier.tier }}
        </span>
        <button
          *ngIf="!tier.editing"
          type="button"
          (click)="startEdit(tier)"
          class="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <lucide-icon name="settings" [size]="11" [strokeWidth]="2"></lucide-icon>
          Edit
        </button>
      </div>

      <!-- Card body -->
      <div class="px-4 py-4">

        <!-- Read mode -->
        <ng-container *ngIf="!tier.editing">
          <div class="grid grid-cols-3 gap-3">
            <div>
              <p class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Max Services</p>
              <p class="mt-1 text-2xl font-bold text-slate-900">{{ tier.maxMonitoredServices }}</p>
            </div>
            <div>
              <p class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Max Members</p>
              <p class="mt-1 text-2xl font-bold text-slate-900">{{ tier.maxTeamMembers }}</p>
            </div>
            <div class="text-right">
              <p class="text-[11px] font-medium uppercase tracking-wide text-slate-400">Monthly Cost</p>
              <p class="mt-1 text-2xl font-bold text-slate-900">
                {{ tier.monthlyCost === 0 ? 'Free' : ('$' + tier.monthlyCost) }}
              </p>
            </div>
          </div>
        </ng-container>

        <!-- Edit mode -->
        <ng-container *ngIf="tier.editing">
          <div class="space-y-3">
            <div>
              <label class="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">Max Monitored Services</label>
              <input
                type="number"
                [(ngModel)]="tier.draftLimit"
                min="1"
                step="1"
                class="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>
            <div>
              <label class="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">Max Team Members</label>
              <input
                type="number"
                [(ngModel)]="tier.draftTeamLimit"
                min="1"
                step="1"
                class="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>
            <div>
              <label class="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-400">Monthly Cost (USD)</label>
              <div class="relative">
                <span class="pointer-events-none absolute inset-y-0 left-0 flex w-7 items-center justify-center text-sm text-slate-400">$</span>
                <input
                  type="number"
                  [(ngModel)]="tier.draftCost"
                  min="0"
                  step="1"
                  class="w-full rounded-lg border border-slate-300 pl-7 pr-3 py-1.5 text-sm font-semibold text-slate-900 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>
            </div>
          </div>
        </ng-container>

        <p *ngIf="tier.updatedAt" class="mt-3 text-[10px] text-slate-400">
          Last updated {{ tier.updatedAt | date:'medium' }}
        </p>
      </div>

      <!-- Success banner -->
      <div *ngIf="tier.saveSuccess" class="flex items-center gap-1.5 border-t border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-medium text-emerald-700">
        <lucide-icon name="circle-check" [size]="11" [strokeWidth]="2"></lucide-icon>
        Saved successfully.
      </div>

      <!-- Edit actions -->
      <div *ngIf="tier.editing" class="border-t border-slate-100 px-4 py-3">
        <div *ngIf="tier.saveError" class="mb-2 text-xs text-red-600">{{ tier.saveError }}</div>
        <div class="flex gap-2">
          <button
            type="button"
            (click)="save(tier)"
            [disabled]="tier.saving"
            class="flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-violet-400"
          >
            <lucide-icon *ngIf="tier.saving" name="loader" [size]="11" class="animate-spin" [strokeWidth]="2"></lucide-icon>
            {{ tier.saving ? 'Saving…' : 'Save' }}
          </button>
          <button
            type="button"
            (click)="cancelEdit(tier)"
            [disabled]="tier.saving"
            class="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

    </div>
  </div>

</div>
